import React from 'react';
import { Link } from 'react-router-dom';

import { Badge, Callout, Step } from './shared';

const KnowledgeBase: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Knowledge Base &amp; ADRs</h2>
    <p className="text-base-content/50 text-sm mb-5">Write down how your project works, and the AI will use it.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Each project has its own Knowledge Base: short documents about how your team works and why the code is built the
      way it is. MFlow&apos;s agents read it before they answer questions, write code, or review a pull request. To open
      it, click <strong>Knowledge</strong> in a Standalone project&apos;s board or analytics menu.
    </p>

    <h3 className="font-semibold mb-2">Document types</h3>
    <div className="overflow-x-auto mb-5">
      <table className="table table-sm text-sm">
        <thead>
          <tr>
            <th>Type</th>
            <th>Use it for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Badge color="badge-info">Metric</Badge>
            </td>
            <td className="text-base-content/70">A KPI or number the AI should understand for this project</td>
          </tr>
          <tr>
            <td>
              <Badge color="badge-primary">Workflow</Badge>
            </td>
            <td className="text-base-content/70">How your team works, such as process, cadence, and handoffs</td>
          </tr>
          <tr>
            <td>
              <Badge>Convention</Badge>
            </td>
            <td className="text-base-content/70">A rule or standard your team follows</td>
          </tr>
          <tr>
            <td>
              <Badge color="badge-success">Guide</Badge>
            </td>
            <td className="text-base-content/70">
              Longer reference docs. These are indexed for AI search, and the doc page shows when indexing is done.
            </td>
          </tr>
          <tr>
            <td>
              <Badge color="badge-secondary">ADR</Badge>
            </td>
            <td className="text-base-content/70">An architecture decision record (see below)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 className="font-semibold mb-2">Architecture Decision Records (ADRs)</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      An ADR records one decision, such as &quot;all services talk over NATS&quot; or &quot;queues use BullMQ
      only&quot;, and why it was made. MFlow checks code and reviews against these decisions.
    </p>
    <div className="space-y-3 mb-5">
      <Step n={1}>
        In the Knowledge Base, create a new document and choose the <strong>ADR</strong> type. The editor fills in the
        three required sections: <code className="bg-base-200 px-1 rounded text-xs">## Context</code>,{' '}
        <code className="bg-base-200 px-1 rounded text-xs">## Decision</code>, and{' '}
        <code className="bg-base-200 px-1 rounded text-xs">## Consequences</code>. You can&apos;t save an ADR without
        all three.
      </Step>
      <Step n={2}>
        Set its status: <Badge>Proposed</Badge> (the default), <Badge color="badge-success">Accepted</Badge>, or{' '}
        <Badge color="badge-warning">Superseded</Badge>. Only <strong>accepted</strong> ADRs are enforced.
      </Step>
    </div>

    <p className="text-sm text-base-content/70 mb-2 leading-relaxed">
      Once an ADR is accepted, MFlow uses it in four places:
    </p>
    <ul className="text-sm text-base-content/70 space-y-2 list-disc list-inside mb-5 leading-relaxed">
      <li>
        <strong>Chat agents</strong> — the Jira, Trello, and Standalone assistants see the Decision section of every
        accepted ADR, and can search the full text when they need more.
      </li>
      <li>
        <strong>Repo sync</strong> — when the{' '}
        <Link to="/docs/coding-agent" className="link link-primary">
          Coding Agent
        </Link>{' '}
        works on a repository, it writes the project&apos;s ADRs to{' '}
        <code className="bg-base-200 px-1 rounded text-xs">docs/adr/NNNN-title.md</code> and updates the{' '}
        <code className="bg-base-200 px-1 rounded text-xs">docs/adr/README.md</code> index as part of the same commit.
        The ADR gets a permanent number the first time it&apos;s synced, shown as <Badge>ADR-0007</Badge>.
      </li>
      <li>
        <strong>PR reviews</strong> — the{' '}
        <Link to="/docs/git-reviewer" className="link link-primary">
          Git Reviewer
        </Link>{' '}
        checks pull requests against your ADRs.
      </li>
      <li>
        <strong>Drift check</strong> — an optional weekly job that checks whether the code still follows each decision
        (see below).
      </li>
    </ul>

    <Callout type="warning">
      Sync only goes one way: from MFlow to the repo. Each sync overwrites the files in{' '}
      <code className="bg-base-200 px-1 rounded text-xs">docs/adr/</code> with the Knowledge Base version, so make
      changes to an ADR in MFlow, not in the repo, or they&apos;ll be lost at the next sync.
    </Callout>

    <p className="text-sm text-base-content/70 mt-4 mb-5 leading-relaxed">
      <strong>One repo or all of them:</strong> an ADR can be tied to a single repository, shown as a repo badge on the
      ADR. It&apos;s then synced to and checked against that repo only. An ADR without a repository applies to every
      repo in the project. Today, agents and the MCP server can set the repository, but the Knowledge Base editor
      can&apos;t yet. Editing an ADR in MFlow keeps any repository that&apos;s already set.
    </p>

    <h3 className="font-semibold mb-2">ADR drift check</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Code changes over time, and decisions can quietly stop being followed. The drift check looks for this. Once a
      week, MFlow takes a few accepted ADRs that haven&apos;t been checked recently, clones the repo each one applies
      to, and has an agent check whether the code still matches the decision.
    </p>
    <div className="space-y-3 mb-5">
      <Step n={1}>
        It&apos;s <strong>off by default</strong>, because it clones your repo and runs an AI over it without you
        starting anything. Turn it on in <strong>Settings → Automation → Architecture decision drift check</strong>.
      </Step>
      <Step n={2}>
        Each check runs as a small background Coding Agent task, listed as{' '}
        <em>🔍 Automated: architecture decision check</em>. It always runs on MFlow&apos;s hosted model, never your own
        key, and doesn&apos;t count toward your monthly coding-agent limits.
      </Step>
      <Step n={3}>
        Results appear on the ADR. The sidebar shows <em>Drift-checked 3 days ago</em> (or{' '}
        <em>Never drift-checked yet</em>), and if the code no longer follows the decision, the ADR shows a{' '}
        <strong>Drift detected</strong> warning with a short explanation.
      </Step>
    </div>

    <Callout type="info">
      Only ADRs tied to a specific repository are drift-checked. MFlow won&apos;t guess which repo an ADR is about, so
      an ADR without a repository is skipped.
    </Callout>
  </div>
);

export default KnowledgeBase;
